/**
 * AddressWithName Component
 * Displays a blockchain address with its X1NS domain name (if available)
 * Supports copy-to-clipboard and links to account pages
 */

import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Copy, Check, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'
import { x1nsService } from '@/services/x1nsService'
import { log } from '@/utils/logger'

interface AddressWithNameProps {
  address: string
  className?: string
  // Display options
  truncateLength?: number
  showFullAddress?: boolean
  showCopyButton?: boolean
  showLinkIcon?: boolean
  // Name display options
  showName?: boolean
  namePosition?: 'before' | 'after' | 'replace'
  // Link behavior
  linkToAccount?: boolean
  linkPath?: string // Override default /account/:address
  // Styling
  monospace?: boolean
  size?: 'xs' | 'sm' | 'md' | 'lg'
}

export const AddressWithName: React.FC<AddressWithNameProps> = ({
  address,
  className = '',
  truncateLength = 8,
  showFullAddress = false,
  showCopyButton = true,
  showLinkIcon = false,
  showName = true,
  namePosition = 'before',
  linkToAccount = true,
  linkPath,
  monospace = true,
  size = 'sm'
}) => {
  const [x1nsName, setX1nsName] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [copied, setCopied] = useState(false)

  // Fetch X1NS name on mount or address change
  useEffect(() => {
    if (!showName || !address) {
      setX1nsName(null)
      return
    }

    let cancelled = false
    setIsLoading(true)

    x1nsService.getPrimaryDomain(address)
      .then(name => {
        if (!cancelled) {
          setX1nsName(name)
          setIsLoading(false)
        }
      })
      .catch(err => {
        if (!cancelled) {
          log.warn('Failed to fetch X1NS name:', err)
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [address, showName])

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    try {
      await navigator.clipboard.writeText(address)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      log.error('Failed to copy address:', error)
    }
  }

  if (!address) {
    return <span className="text-gray-400">-</span>
  }

  // Format address for display
  const truncatedAddress = showFullAddress
    ? address
    : address.length > truncateLength * 2 + 3
      ? `${address.slice(0, truncateLength)}...${address.slice(-truncateLength)}`
      : address

  // Size classes
  const sizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  }

  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  }

  // Build the display content
  const renderContent = () => {
    const addressDisplay = (
      <span className={clsx(monospace && 'font-mono', 'text-inherit')}>
        {truncatedAddress}
      </span>
    )

    // If we have a name and it should replace the address
    if (x1nsName && namePosition === 'replace') {
      return (
        <span className="flex items-center gap-1.5">
          <span className="text-primary font-medium">{x1nsName}</span>
          <span className="text-gray-400 text-xs font-mono">
            ({address.slice(0, 4)}...{address.slice(-4)})
          </span>
        </span>
      )
    }

    // If we have a name and it should be shown before/after
    if (x1nsName) {
      return (
        <span className="flex items-center gap-1.5">
          {namePosition === 'before' && (
            <span className="text-primary font-medium">{x1nsName}</span>
          )}
          {addressDisplay}
          {namePosition === 'after' && (
            <span className="text-primary font-medium">({x1nsName})</span>
          )}
        </span>
      )
    }

    // Loading state
    if (isLoading && showName) {
      return (
        <span className="flex items-center gap-1.5">
          {addressDisplay}
          <span className="w-12 h-3 bg-gray-200 dark:bg-gray-700 animate-pulse rounded" />
        </span>
      )
    }

    // Just the address
    return addressDisplay
  }

  const content = (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5',
        sizeClasses[size],
        className
      )}
    >
      {renderContent()}

      {showCopyButton && (
        <button
          onClick={handleCopy}
          className={clsx(
            'inline-flex items-center justify-center p-0.5 rounded transition-colors',
            'hover:bg-gray-100 dark:hover:bg-gray-800',
            copied
              ? 'text-green-600 dark:text-green-400'
              : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
          )}
          title={copied ? 'Copied!' : 'Copy address'}
        >
          {copied ? (
            <Check className={iconSizes[size]} />
          ) : (
            <Copy className={iconSizes[size]} />
          )}
        </button>
      )}

      {showLinkIcon && linkToAccount && (
        <ExternalLink className={clsx(iconSizes[size], 'text-gray-400')} />
      )}
    </span>
  )

  // Wrap in Link if needed
  if (linkToAccount) {
    const href = linkPath || `/account/${address}`
    return (
      <Link
        to={href}
        className={clsx(
          'hover:text-primary transition-colors',
          'inline-flex items-center'
        )}
      >
        {content}
      </Link>
    )
  }

  return content
}

/**
 * Simplified version just for displaying the X1NS name badge
 */
interface X1nsNameBadgeProps {
  address: string
  className?: string
  fallback?: React.ReactNode
}

export const X1nsNameBadge: React.FC<X1nsNameBadgeProps> = ({
  address,
  className = '',
  fallback = null
}) => {
  const [name, setName] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!address) {
      setIsLoading(false)
      return
    }

    let cancelled = false

    x1nsService.getPrimaryDomain(address)
      .then(result => {
        if (!cancelled) {
          setName(result)
          setIsLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [address])

  if (isLoading) {
    return (
      <span className={clsx('inline-block w-16 h-4 bg-gray-200 dark:bg-gray-700 animate-pulse rounded', className)} />
    )
  }

  if (!name) {
    return <>{fallback}</>
  }

  return (
    <span
      className={clsx(
        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
        'bg-primary/10 text-primary',
        className
      )}
    >
      {name}
    </span>
  )
}

/**
 * Hook for fetching X1NS name
 */
export function useX1nsName(address: string | null | undefined): {
  name: string | null
  isLoading: boolean
  error: Error | null
} {
  const [name, setName] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!address) {
      setName(null)
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)

    x1nsService.getPrimaryDomain(address)
      .then(result => {
        if (!cancelled) {
          setName(result)
          setIsLoading(false)
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err)
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [address])

  return { name, isLoading, error }
}

/**
 * Hook for fetching all domains owned by an address
 */
export function useX1nsDomains(address: string | null | undefined): {
  domains: Array<{ name: string; address: string }>
  isLoading: boolean
  error: Error | null
} {
  const [domains, setDomains] = useState<Array<{ name: string; address: string }>>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!address) {
      setDomains([])
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)

    x1nsService.getDomainsByOwner(address)
      .then(result => {
        if (!cancelled) {
          setDomains(result)
          setIsLoading(false)
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err)
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [address])

  return { domains, isLoading, error }
}

export default AddressWithName
